"use client";

import { IconCircleInfoOutline18 } from "nucleo-ui-outline-18";
import { useContractProject } from "@/hooks/useContractProject";
import { cn } from "@/lib/utils";
import type { ContractProject } from "@/types/contract";
import type { Edge, Node } from "@xyflow/react";
import { PixelLoader } from "@/components/ui/pixel-loader";
import * as React from "react";
import VariableInput from "./VariableInput";

// ABI parameter type
interface AbiParameter {
  name: string;
  type: string;
  internalType?: string;
  components?: AbiParameter[]; // For tuple types
}

// Constructor ABI entry
interface ConstructorAbi {
  type: "constructor";
  inputs: AbiParameter[];
  stateMutability?: string;
}
interface ConstructorArgsInputProps {
  label?: string;
  description?: string;
  value: string | undefined;
  onChange: (value: string) => void;
  /** Contract project ID (to fetch ABI if not provided) */
  contractProjectId?: string;
  /** Raw ABI string (from project selection or custom input) */
  abiString?: string;
  /** Current contractSource mode */
  contractSource?: string;
  // For variable support
  currentNodeId?: string;
  allNodes?: Node[];
  allEdges?: Edge[];
  nodeDefinitions?: {
    type: string;
    resultSchema?: {
      properties?: Record<string, {
        description?: string;
        type?: string;
      }>;
    };
  }[];
}

/**
 * Constructor Args Input
 *
 * Dynamically generates form fields based on the contract's constructor parameters.
 * When a project is selected, parses its ABI to detect constructor inputs.
 * Shows typed inputs for each parameter (address, uint256, string, etc.)
 */
function ConstructorArgsInput({
  label,
  description,
  value,
  onChange,
  contractProjectId,
  abiString,
  contractSource,
  currentNodeId,
  allNodes = [],
  allEdges = [],
  nodeDefinitions = []
}: ConstructorArgsInputProps) {
  // Fetch project data if we have an ID but no ABI (e.g., page reload)
  const shouldFetchProject = contractSource === "project" && !!contractProjectId && !abiString;
  const {
    project,
    isLoading: isLoadingProject
  } = useContractProject({
    projectId: shouldFetchProject ? contractProjectId : undefined,
    autoFetch: shouldFetchProject
  });

  // Parse constructor parameters from ABI
  const constructorParams = React.useMemo((): AbiParameter[] => {
    let abi: unknown[] = [];

    // Priority: abiString (from selection) > fetched project > empty
    if (abiString) {
      try {
        abi = JSON.parse(abiString);
      } catch {
        return [];
      }
    } else if (project?.source?.abi) {
      abi = project.source.abi;
    } else if (project?.source?.constructorAbi) {
      // If constructorAbi is available, it's already the constructor inputs
      return project.source.constructorAbi as AbiParameter[];
    }
    if (!Array.isArray(abi)) return [];

    // Find constructor in ABI
    const constructor = abi.find((item): item is ConstructorAbi => typeof item === "object" && item !== null && (item as Record<string, unknown>).type === "constructor");
    return constructor?.inputs || [];
  }, [project, abiString]);

  // Parse current args value into array
  const currentArgs = React.useMemo((): string[] => {
    if (!value) return [];
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch {
      return [];
    }
  }, [value]);

  // Update a specific argument
  const updateArg = (index: number, newValue: string) => {
    const newArgs = [...currentArgs];
    // Extend array if needed
    while (newArgs.length <= index) {
      newArgs.push("");
    }
    newArgs[index] = newValue;
    onChange(JSON.stringify(newArgs));
  };

  // Get placeholder based on type
  const getPlaceholder = (type: string): string => {
    if (type.startsWith("uint") || type.startsWith("int")) {
      return "0";
    }
    if (type === "address") {
      return "0x...";
    }
    if (type === "bool") {
      return "true or false";
    }
    if (type.startsWith("bytes")) {
      return "0x...";
    }
    if (type === "string") {
      return "Enter text...";
    }
    if (type.endsWith("[]")) {
      return '["item1", "item2"]';
    }
    return "Enter value...";
  };

  // Get input type hint
  const getTypeHint = (type_0: string): string => {
    if (type_0.startsWith("uint")) {
      const bits = type_0.replace("uint", "") || "256";
      return `Unsigned integer (${bits} bits)`;
    }
    if (type_0.startsWith("int")) {
      const bits_0 = type_0.replace("int", "") || "256";
      return `Signed integer (${bits_0} bits)`;
    }
    if (type_0 === "address") {
      return "Ethereum address (0x...)";
    }
    if (type_0 === "bool") {
      return "Boolean (true/false)";
    }
    if (type_0.startsWith("bytes32")) {
      return "32-byte hex string";
    }
    if (type_0.startsWith("bytes")) {
      return "Hex-encoded bytes";
    }
    if (type_0 === "string") {
      return "UTF-8 string";
    }
    if (type_0.endsWith("[]")) {
      return `Array of ${type_0.slice(0, -2)}`;
    }
    return type_0;
  };

  // Loading state when fetching project
  if (isLoadingProject && contractSource === "project") {
    return <div className="border-ds-border-default dark:bg-ds-neutral-800/50 flex items-center gap-2 rounded-lg border bg-zinc-50 px-3 py-2">
        <PixelLoader size="sm" color="blue" />
        <span className="body-sm">Loading constructor parameters...</span>
      </div>;
  }

  // No constructor params - show nothing or minimal message
  if (constructorParams.length === 0) {
    // If using project mode and we have ABI data (either from selection or fetch), show "no args needed"
    if (contractSource === "project" && (abiString || project?.source?.abi)) {
      return <div className="border-ds-border-default dark:bg-ds-neutral-800/50 flex items-center gap-2 rounded-lg border bg-zinc-50 px-3 py-2">
          <IconCircleInfoOutline18 className="text-ds-text-muted h-4 w-4" />
          <span className="body-sm">This contract has no constructor arguments</span>
        </div>;
    }

    // In custom mode without ABI, show raw input
    if (contractSource === "custom" || !contractSource) {
      return <div className="space-y-2">
          {label && <label className="field-label block">{label}</label>}
          {description && <p className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">{description}</p>}
          <VariableInput label="" description="" value={value || ""} onChange={onChange} placeholder='["arg1", 1000, "0x..."] or leave empty' currentNodeId={currentNodeId} allNodes={allNodes} allEdges={allEdges} nodeDefinitions={nodeDefinitions} />
          <p className="label-xs">JSON array of constructor arguments. Provide ABI to get typed inputs.</p>
        </div>;
    }
    return null;
  }

  // Show typed inputs for each constructor parameter
  return <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          {label && <label className="field-label block">{label || "Constructor Arguments"}</label>}
          {description && <p className="text-ds-text-subtle dark:text-ds-text-disabled text-xs">{description}</p>}
        </div>
        <span className="label-xs">
          {constructorParams.length} parameter{constructorParams.length !== 1 ? "s" : ""}
        </span>
      </div>

      <div className="border-ds-border-default dark:bg-ds-neutral-800/30 space-y-3 rounded-lg border bg-zinc-50/50 p-3">
        {constructorParams.map((param, index_0) => <div key={index_0} className="space-y-1">
            <div className="flex items-center gap-2">
              <label className="field-label-muted">{param.name || `arg${index_0}`}</label>
              <span className="bg-ds-neutral-200 text-ds-text-subtle dark:bg-ds-neutral-700 dark:text-ds-text-disabled text-3xs rounded px-1.5 py-0.5 font-mono">
                {param.type}
              </span>
            </div>
            <VariableInput label="" description="" value={currentArgs[index_0] || ""} onChange={newValue_0 => updateArg(index_0, newValue_0)} placeholder={getPlaceholder(param.type)} currentNodeId={currentNodeId} allNodes={allNodes} allEdges={allEdges} nodeDefinitions={nodeDefinitions} />
            <p className="text-ds-text-muted dark:text-ds-text-muted text-3xs">{getTypeHint(param.type)}</p>
          </div>)}
      </div>

      {/* Show raw JSON preview */}
      <div className="label-xs flex items-center gap-2">
        <span>Output:</span>
        <code className="bg-ds-neutral-100 dark:bg-ds-neutral-800 rounded px-2 py-0.5 font-mono">{value || "[]"}</code>
      </div>
    </div>;
}
export default React.memo(ConstructorArgsInput);